import { useEffect } from "react";

import { FormActions } from "../../common/FormActions";
import FormFields from "../../common/FormFields";
import { IFormSection, TFormHookParams } from "../../../types/common";
import {
  createColumn,
  createViewAction,
  getRegister,
  formatDateTime,
  numberFormat,
} from "../../../utils/helpers";
import { URL_TRANSACTABLE_ACCOUNTS_DETAILS } from "../../../utils/urls";
import useGet from "../../../hooks/common/useGet";
import { API_TRANSACTABLE_ACCOUNTS_DETAILS } from "../../../utils/apis";

// Helper: Format Amount
const formatAmount = (amount: string | number) => {
  return `UGX ${Number(amount).toLocaleString("en-US")}`;
};

export const useTransactableAccountDetails = () => {
  const tableColumns = () => [
    createColumn("account_name", true, "Account Name"),
    createColumn("account_code", true, "Account Code"),
    createColumn("account_type", true, "Account Type"),
    createColumn("account_balance", false, "Balance"),
    createColumn("balance_status", false, "Status"),
    createColumn("actions", false, "Actions"),
  ];

  const tableData = (data: any) => {
    if (!data) return [];

    return data.map((item: any) => {
      const balance = parseFloat(item?.account_balance ?? 0);
      const balanceStatus =
        balance > 0 ? "Credit" : balance < 0 ? "Debit" : "Zero";

      return {
        ...item,
        account_balance: numberFormat(balance),
        balance_status: balanceStatus,
        actions: (
          <FormActions
            actions={[
              createViewAction(
                URL_TRANSACTABLE_ACCOUNTS_DETAILS,
                item?.account_code,
              ),
            ]}
          />
        ),
      };
    });
  };

  const viewData = (item: any) => {
    if (!item) {
      return {
        sections: [
          {
            title: "Error",
            fields: { message: "No data available" },
          },
        ],
      };
    }

    const balance = parseFloat(item?.account_balance ?? 0);
    const balanceStatus =
      balance > 0
        ? "Credit Balance"
        : balance < 0
          ? "Debit Balance"
          : "Zero Balance";

    return {
      sections: [
        {
          title: "Account Details",
          fields: {
            account_name: item?.account_name || "N/A",
            account_code: item?.account_code || "N/A",
            account_type: item?.account_type || "N/A",
            account_balance: formatAmount(balance),
            balance_status: balanceStatus,
          },
        },
        {
          title: "System Information",
          fields: {
            created_at: item?.created_at
              ? formatDateTime(item.created_at)
              : "N/A",
            updated_at: item?.updated_at
              ? formatDateTime(item.updated_at)
              : "N/A",
            last_transaction: item?.last_transaction_date
              ? formatDateTime(item.last_transaction_date)
              : "No transactions",
            status: item?.status || "Active",
          },
        },
      ],
    };
  };

  return {
    tableData,
    tableColumns,
    viewData,
    // formRoute,
  };
};

export const useTransactableAccountDetailsForm = ({
  updateFormValues,
}: TFormHookParams = {}) => {
  const { loadData, loading, data } = useGet();

  useEffect(() => {
    loadData({ api: API_TRANSACTABLE_ACCOUNTS_DETAILS });
  }, []);

  useEffect(() => {
    if (updateFormValues) {
      updateFormValues({
        account_type: "AGENT", // Set default account type
      });
    }
  }, [updateFormValues]);

  const accountTypeOptions = [
    { value: "AGENT", label: "Agent" },
    { value: "MERCHANT", label: "Merchant" },
    { value: "CUSTOMER", label: "Customer" },
    { value: "SYSTEM", label: "System Account" },
  ];

  const statusOptions = [
    { value: "ACTIVE", label: "Active" },
    { value: "INACTIVE", label: "Inactive" },
    { value: "SUSPENDED", label: "Suspended" },
    { value: "CLOSED", label: "Closed" },
  ];

  const sections: IFormSection[] = [
    {
      title: "Account Information",
      fields: [
        {
          field_name: "account_name",
          label: "Account Name",
          type: "text",
          props: {
            required: true,
            placeholder: "Enter account name",
            maxLength: 100,
          },
        },
        {
          field_name: "account_code",
          label: "Account Code",
          type: "text",
          props: {
            required: true,
            placeholder: "Auto-generated or enter custom code",
            maxLength: 50,
          },
        },
        {
          field_name: "account_type",
          label: "Account Type",
          type: "select",
          props: { required: true },
          options_data: accountTypeOptions,
        },
        {
          field_name: "account_balance",
          label: "Initial Balance (UGX)",
          type: "number",
          props: {
            step: 0.01,
            placeholder: "Enter initial balance (optional)",
            min: 0,
          },
        },
      ],
    },
    {
      title: "Account Settings",
      fields: [
        {
          field_name: "status",
          label: "Account Status",
          type: "select",
          props: { required: true },
          options_data: statusOptions,
        },
        {
          field_name: "description",
          label: "Description",
          type: "textarea",
          props: {
            rows: 3,
            placeholder: "Enter account description (optional)",
            maxLength: 500,
          },
        },
        {
          field_name: "notes",
          label: "Additional Notes",
          type: "textarea",
          props: {
            rows: 2,
            placeholder: "Any additional notes about this account",
            maxLength: 250,
          },
        },
      ],
    },
  ];

  const getConfigOptions = () => {
    return {
      accountTypes: accountTypeOptions,
      statuses: statusOptions,
    };
  };

  const options = {
    ...getConfigOptions(),
  };

  const title = "Transactable Account Management";
  const redirect = getRegister(URL_TRANSACTABLE_ACCOUNTS_DETAILS);

  return {
    sections,
    title,
    redirect,
    options,
    loading,
    data,
  };
};

export const TransactableAccountDetailsForm = ({
  form,
  error,
  updateFormValue,
  onChange,
}: any) => {
  const { sections } = useTransactableAccountDetailsForm({
    form,
    updateFormValues: updateFormValue,
  });

  return (
    <div>
      {sections.map((section: IFormSection, index: number) => (
        <div key={index}>
          <h3>{section.title}</h3>
          <FormFields
            form={form}
            error={error}
            updateFormValue={updateFormValue}
            onChange={onChange}
            fields={section.fields}
          />
        </div>
      ))}
    </div>
  );
};
